import { computed } from 'vue'

import type { ComputedRef } from 'vue'

import { useLessonRoomStore } from '@stores/lesson-room'
import { useKidStore } from '@stores/kid'

import { useRoomLiveKitChat } from '@hooks/classroom/chat-live-kit.hook'
import { useRoomLiveKit } from '@hooks/classroom/room-live-kit.hook'
import { useKidInCustomer } from '@hooks/kid/kid-in-customer.hook'

import amplitudeConfig from '@config/amplitude.json'

import { trackClassroomEvent } from '@tools/amplitude/classroom'

interface ClassroomChatReturn {
  isReady: ComputedRef<boolean>
  sendMessage: (text: string) => Promise<void>
  toggleChat: () => void
}

export const useClassroomChat = (): ClassroomChatReturn => {
  const lessonRoomStore = useLessonRoomStore()
  const kidStore = useKidStore()
  const { name: studentName } = useKidInCustomer({ kidId: kidStore.activeKidId })
  const { connectionState } = useRoomLiveKit()

  const { sendMessage } = useRoomLiveKitChat({
    getSenderName: () => studentName.value,
    onMessage: (m) => lessonRoomStore.addChatMessage(m),
    onSent: () => trackClassroomEvent(amplitudeConfig.events.clickChat, { action: 'send' }),
  })

  const isReady = computed<boolean>(() => connectionState.value === 'connected')

  const toggleChat = (): void => {
    const next = !lessonRoomStore.isChatOpen
    lessonRoomStore.setChatOpen(next)
    if (next) lessonRoomStore.setChatUnread(false)
  }

  return {
    isReady,
    sendMessage,
    toggleChat,
  }
}
